/**
 * Button Component
 *
 * A versatile button component that wraps the ShadCN button component.
 * Supports different visual variants and click handling.
 */
import * as React from 'react';

import { Button as ShadcnButton } from '@ui/components/ui/button';
import { cn } from '@ui/lib/utils';

export interface ButtonProps {
  /**
   * Button text content
   * This text will be displayed inside the button
   */
  label: string;

  /**
   * Optional click handler
   * Function to be called when the button is clicked
   */
  onClick?: () => void;

  /**
   * Visual variant of the button
   * - primary: Orange background with white text, changes to light orange on hover
   * - secondary: Light gray background with dark text, changes to medium gray on hover
   * - text: No background with primary color text, subtle hover effect
   * - link: Transparent background with primary-500 text, hover to primary-600
   * @default 'primary'
   */
  variant?: 'primary' | 'secondary' | 'text' | 'link';

  /**
   * Size variant of the button
   * - default: Standard size
   * - sm: Small size
   * - lg: Large size
   * - icon: Square icon button
   * @default 'default'
   */
  size?: 'default' | 'sm' | 'lg' | 'icon';

  /**
   * Optional disabled state
   * If true, the button will be visually disabled and non-interactive
   * @default false
   */
  disabled?: boolean;

  /**
   * Optional additional class names
   */
  className?: string;

  /**
   * Optional type attribute for the button element
   * @default 'button'
   */
  type?: 'button' | 'submit' | 'reset';

  /**
   * Optional children elements
   * If provided, these will be rendered instead of the label
   */
  children?: React.ReactNode;

  /**
   * Whether to render the button as a child
   * When true, the component will render its child as the root element
   * @default false
   */
  asChild?: boolean;
}

/**
 * Button component for user interaction
 *
 * @example
 * ```tsx
 * <Button
 *   label="Click Me"
 *   variant="primary"
 *   onClick={() => console.log('Button clicked')}
 * />
 * ```
 */
export const Button = ({
  label,
  variant = 'primary',
  size = 'default',
  onClick,
  disabled = false,
  className,
  type = 'button',
  children,
  asChild = false,
  ...props
}: ButtonProps) => {
  // Map our custom variants to ShadCN variants
  const variantMap = {
    primary: 'default',
    secondary: 'secondary',
    text: 'ghost',
    link: 'link'
  } as const;

  // Custom styling based on our design system
  const customClasses =
    variant === 'primary'
      ? 'bg-primary-500 hover:bg-primary-400 text-white'
      : variant === 'secondary'
        ? 'bg-neutral-100 text-neutral-800 hover:bg-neutral-500 hover:text-white'
        : variant === 'text'
          ? 'bg-transparent text-primary-400 hover:text-primary-500'
          : variant === 'link'
            ? 'p-0 h-auto gap-1 bg-transparent text-primary-500 hover:text-secondary-500 no-underline hover:no-underline'
            : '';

  return (
    <ShadcnButton
      variant={variantMap[variant]}
      size={size}
      onClick={onClick}
      disabled={disabled}
      type={type}
      asChild={asChild}
      className={cn(
        'cursor-pointer font-semibold font-manrope',
        size === 'lg' ? 'text-lg' : 'text-base',
        customClasses,
        className
      )}
      {...props}
    >
      {children || label}
    </ShadcnButton>
  );
};
